import React, { useState } from "react";
import { useMediaQuery, Breadcrumbs, Snackbar, Theme, Tooltip } from "@mui/material";
import {
  BoxBreadCrumbs,
  StyledLink,
  StyledSeperator,
} from "./MarketPlaceBreadCrumbs.style";
import { Star } from "@mui/icons-material";
import { StyledIcon } from "component/commonComponents/Icon/icon.style";
import DefaultUptimeTheme from "component/theme/defaultTheme";
import { useTheme } from "styled-components";
import Typography from "../Typography/Typography";
import { Box } from "@mui/system";
import { BreadcrumbShareButtonIcon } from "component/commonComponents/Icon/BreadcrumbShareButtonIcon";

export interface IBreadCrumbProps {
  breadPath: string;
  breadIcons?: any;
  onClick: (item: string) => void;
  isOpen: boolean;
  isDefaultSeperator?: boolean;
  isMaxBread?: boolean;
  isShowSubBreadIcon?: boolean;
  pernamentLink?: string;
  whitelistUrl?: string[];
  locale?: Record<string, any>;
}

const type = {
  disabled: [
    'personal',
    'operation',
    'custom-dashboard',
    'experience',
    'my-activities',
    'automated-service-request',
    'pc-refresh',
    'pc-return',
    'pc-report-broken',
    'i-dont-have-this-device',
    'pc-lost-stolen',
    'hardware',
    'software',
    'catalogs',
  ],
  Nondisplay: ['detail', 'category', 'product-detail','computers',],
  itemType: {
    'request-tickets': 'Requests',
    'view-order': 'Requests',
  } as Record<string, string>,
};

const SHARE_BUTTON_SIZE = 28;

const MarketPlaceBreadCrumbs: React.FC<IBreadCrumbProps> = (props) => {
  const {
    breadPath,
    breadIcons,
    onClick,
    isOpen,
    isDefaultSeperator,
    isMaxBread,
    isShowSubBreadIcon,
    pernamentLink,
    whitelistUrl,
    locale = {},
  } = props;
  let crumbs = breadPath.split("/");
  crumbs[0] = "Home";
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [onHover, setOnHover] = useState(false);

  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const StyledSubBreadIcon = (item: any) => (
    <StyledIcon
      styleIcon={{
        fontSize: `${theme.uptimeFontSizes.desktop.fz5}`,
        color: `${theme.uptimeColors.grayScales.darkerGray} !important`,
        width: `${theme.uptimeSpace[8]}`,
        height: `${theme.uptimeSpace[8]}`,
        marginRight: `${theme.uptimeSpace[4] + 1}`,
      }}
    >
      {item}
    </StyledIcon>
  );

  const customBreadIcon = (crumb: string) => {
    let result;

    if (breadIcons.length > 0) {
      result = breadIcons.find((item: any) => item.key === crumb);
      if (result !== undefined) return StyledSubBreadIcon(result?.value);
      else return "";
    } else return StyledSubBreadIcon(<Star />);
  };

  const handleClickBreadscrumb = (scrumb: string) => {
    const url = customURL(scrumb);

    return onClick(url);
  };

  const customURL = (crumb: string) => {
    let currentUrl = "";

    if (crumb === crumbs[0]) currentUrl = "/";
    else {
      const crumbsArr = crumbs.filter((item) => item !== crumbs[0]);
      const index = crumbsArr.indexOf(crumb);
      crumbsArr
        .slice(0, index + 1)
        .forEach((item) => (currentUrl += `/${item}`));
    }

    return currentUrl;
  };

  const mappingRoutes: Record<string, string> = {
    "/knowledge articles": "/support/knowledge"
  };

  const checkValidUrl = (crumb: string) => {
    const url = customURL(crumb);

    if (whitelistUrl) {
      if (mappingRoutes.hasOwnProperty(url) && whitelistUrl.includes(mappingRoutes[url])) {
        return true;
      }

      return whitelistUrl.includes(url);
    };

    if (type.disabled.includes(crumb.toLowerCase())) {
      return false;
    };

    return true;
  };

  const breadFilter = crumbs.filter(
    (crumb) => !type.Nondisplay.includes(crumb.toLowerCase())
  );
  const breadByUI = isSmDown ? [breadFilter[0], '...', breadFilter[breadFilter.length - 1]] : breadFilter;

  const customDisplay = (crumb: string) => (Object.values(type.itemType[crumb]).join(""));

  const translateCrumb = (crumb: string) => {
    const changedVerbiage = Object.keys(type.itemType).includes(crumb.toLowerCase()) ? 
      customDisplay(crumb.toLowerCase()) : crumb;
    return locale.category?.[changedVerbiage] || locale[changedVerbiage.toLowerCase()] || changedVerbiage;
  };

  const handleShowBreadCrumb = breadByUI.map(
    (crumb: string, index: number) => {
      const isDisabled = breadByUI.length - 1 === index || !checkValidUrl(crumb);
      const isThreeDots = crumb === '...';
      if (isDisabled || isThreeDots) {
        return (
          <StyledLink key={index} tabIndex={0} role="link">
            {isShowSubBreadIcon ? customBreadIcon(crumb) : ""}
            <Typography
              variant="body2"
              color="text.primary"
              textTransform="capitalize"
            >
              {translateCrumb(crumb)}
            </Typography>
          </StyledLink>
        );
      } else {
        return (
          <StyledLink key={index} tabIndex={0} role="link">
            {isShowSubBreadIcon ? customBreadIcon(crumb) : ""}
            <Typography
              sx={{
                cursor: "pointer",
                textTransform: "capitalize",
                "&:hover": {
                  textDecoration: "underline",
                  color: "primary.main",
                },
              }}
              variant="body2"
              color="text.secondary"
              onClick={() => handleClickBreadscrumb(crumb)}
            >
              {translateCrumb(crumb)}
            </Typography>
          </StyledLink>
        );
      }
    }
  );

  return (
    <>
      {isOpen && (
        <BoxBreadCrumbs>
          <Breadcrumbs
            aria-label="breadcrumb"
            maxItems={isMaxBread ? 10 : 3} // amount item display in breadcrumb
            separator={isDefaultSeperator ? <StyledSeperator /> : "/"}
          >
            {handleShowBreadCrumb}
          </Breadcrumbs>
          {!!pernamentLink && (
            <>
              <Tooltip title={locale?.copyToClipboard} arrow placement="right">
                <Box
                  sx={{
                    width: SHARE_BUTTON_SIZE,
                    height: SHARE_BUTTON_SIZE,
                    cursor: "pointer",
                  }}
                  onMouseEnter={() => setOnHover(true)}
                  onMouseLeave={() => setOnHover(false)}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}${pernamentLink}`
                    );
                    setOpenSnackbar(true);
                  }}
                  role="button"
                  id="btn-breadcrumbs-share"
                >
                  <BreadcrumbShareButtonIcon
                    width={SHARE_BUTTON_SIZE}
                    height={SHARE_BUTTON_SIZE}
                    color={theme.uptimeColors.grayScales.white}
                    backgroundColor={
                      onHover ? theme.uptimeColors.generalColors.icon.enabled : "#707070"
                    }
                  />
                </Box>
              </Tooltip>
              <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                open={openSnackbar}
                autoHideDuration={3000}
                message={locale?.copied}
                onClose={() => setOpenSnackbar(false)}
              />
            </>
          )}
        </BoxBreadCrumbs>
      )}
    </>
  );
};

export const defaultProps = {
  breadPath: "/support/my-technology/knowledge/detail/KB0010188",
  breadIcons: [
    {
      key: "home",
      value: <Star />,
    },
    {
      key: "support",
      value: <Star />,
    },
    {
      key: "my-technology",
      value: <Star />,
    },
    {
      key: "knowledges",
      value: <Star />,
    },
    {
      key: "detail",
      value: <Star />,
    },
    {
      key: "KB0010188",
      value: <Star />,
    },
  ],
  onClick: () => {},
  isOpen: true,
  isDefaultSeperator: false,
  isMaxBread: false,
  isShowSubBreadIcon: false,
  locale: { category: {} },
};

MarketPlaceBreadCrumbs.defaultProps = defaultProps;

export default MarketPlaceBreadCrumbs;
